import { invokeEdge, EdgeResult } from '@/lib/edgeInvoke';

/**
 * SINGLE SOURCE for support-case tooling.
 *
 *  - Admin actions go to `admin-customer-support`, which re-verifies the caller
 *    server-side (JWT + mkt_profiles.is_admin + revoked sessions + MFA when
 *    enforced). Nothing here is trusted by the server.
 *  - The customer "Resend setup email" link goes to `onboarding-resend`, which
 *    only acts on the caller's own account and only when they hold credits but
 *    have never unlocked a lead. Each source (admin / self) may resend once.
 */

export type Row = Record<string, any>;

export interface CustomerCase {
  ok: boolean;
  profile: Row;
  auth: { created_at: string | null; last_sign_in_at: string | null; email_confirmed_at: string | null; banned_until: string | null } | null;
  balance: { extra_credits: number; plan_credits: number; total: number; ledger_sum: number };
  onboarding: { original_sent: boolean; admin_resent: boolean; self_resent: boolean; never_unlocked: boolean };
  subscriptions: Row[];
  payments: Row[];
  orders: Row[];
  events: Row[];
  quarantine: Row[];
  failures: Row[];
  ledger: Row[];
  purchases: Row[];
  emails: Row[];
  notifications: Row[];
  incidents: Row[];
  refunds: Row[];
  actions: Row[];
  /** The customer's payment reconciliation records (matched, claimant, or same payer email). */
  reconciliation?: ReconRecord[];
}

export interface ReconRecord {
  id: string;
  provider_txn_id: string;
  state: string;
  amount: number;
  currency: string;
  plan_code: string | null;
  product: string | null;
  paid_at: string | null;
  first_seen_at: string;
  resolved_at: string | null;
  resolution: string | null;
  is_test: boolean;
  is_duplicate_report: boolean;
  fulfilled: boolean;
  linkable: boolean;
}

export type GrantBasis = 'recon' | 'goodwill';

export interface GrantResult {
  ok: boolean;
  duplicate?: boolean;
  basis?: GrantBasis | 'removal';
  credits_granted?: number;
  recon_records_audited?: number;
  ledger_id: string;
  before?: number;
  after?: number;
  balance: number;
  incident_linked?: boolean;
}

export interface ResendStatus {
  ok: boolean;
  balance: number;
  neverUnlocked: boolean;
  alreadyResent: boolean;
  lastResentAt: string | null;
  eligible: boolean;
}

const FN = 'admin-customer-support';

export const searchCustomers = (q: string) =>
  invokeEdge<{ ok: boolean; results: Row[]; ghl_configured?: boolean }>(FN, { action: 'search', q });

/** GoHighLevel contact (lead) as returned by the admin-only `ghl-search` action. */
export interface GhlContact {
  id: string | null;
  email: string | null;
  phone: string | null;
  name: string | null;
  company: string | null;
  tags: string[];
  source: string | null;
  date_added: string | null;
  giggone: boolean;
  giggone_user_id: string | null;
  giggone_email: string | null;
}

/** Search GoHighLevel by contact ID, email, phone or name (fallback when no GigGone account matches). */
export const searchGhlContacts = (q: string) =>
  invokeEdge<{ ok: boolean; contacts: GhlContact[] }>(FN, { action: 'ghl-search', q });

/** Email a GHL-only lead a prefilled registration link. Audited server-side. */
export const inviteToRegister = (p: { email: string; name?: string; ghlContactId?: string; reason: string }) =>
  invokeEdge<{ ok: boolean; sent: boolean; to: string; link: string }>(FN, {
    action: 'invite-registration',
    origin: typeof window !== 'undefined' ? window.location.origin : undefined,
    ...p,
  });

export const loadCustomerCase = (target: { userId?: string; email?: string }) =>
  invokeEdge<CustomerCase>(FN, { action: 'case', ...target });

export const grantCredits = (p: {
  userId: string;
  delta: number;
  reason: string;
  incidentRef: string;
  requestId: string;
  /** Required for additions: link to an unfulfilled payment record, or explicit goodwill. */
  basis?: GrantBasis;
  reconId?: string;
}) => invokeEdge<GrantResult>(FN, { action: 'grant', ...p });

export const adminResendOnboarding = (p: { userId: string; reason: string; incidentRef?: string }) =>
  invokeEdge<{ ok: boolean; sent: boolean; skipped?: string; to?: string; logId?: string }>(FN, {
    action: 'resend-onboarding',
    ...p,
  });

export const selfResendStatus = () => invokeEdge<ResendStatus>('onboarding-resend', { action: 'status' });
export const selfResendOnboarding = () =>
  invokeEdge<{ ok: boolean; sent: boolean; skipped?: string; to?: string }>('onboarding-resend', { action: 'send' });

/** Human sentence for a server error code. */
export const supportErrorText = (r: Pick<EdgeResult, 'error' | 'reason' | 'message'>, es = false): string => {
  const map: Record<string, [string, string]> = {
    invalid_delta: ['Enter a whole number between -100 and 100 (not 0).', 'Ingrese un número entero entre -100 y 100 (no 0).'],
    reason_required: ['A reason of at least 8 characters is required.', 'Se requiere un motivo de al menos 8 caracteres.'],
    incident_ref_required: ['An incident reference is required (e.g. SUP-FCLONZ74-20260924).', 'Se requiere una referencia de incidente (p. ej. SUP-FCLONZ74-20260924).'],
    insufficient_extra_credits: ['The customer does not have that many extra credits to remove.', 'El cliente no tiene tantos créditos extra para retirar.'],
    customer_not_found: ['No account matches that customer.', 'Ninguna cuenta coincide con ese cliente.'],
    mfa_required: ['Complete your second factor (MFA) and try again.', 'Complete su segundo factor (MFA) e intente de nuevo.'],
    suppressed: ['This address is on the suppression list (bounce/complaint). Release it in Email delivery first.', 'Esta dirección está suprimida (rebote/queja). Libérela primero en Entrega de correo.'],
  };
  const hit = r.error ? map[r.error] : null;
  if (hit) return es ? hit[1] : hit[0];
  return r.message || (es ? 'La acción falló.' : 'The action failed.');
};

export const newRequestId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  ).replace(/[^a-z0-9-]/gi, '');

export const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString() : '—');
export const fmtMoney = (cents?: number | null) =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—';
